import { PROMOTION_TYPE } from './promotionTypes.js'
import { resolveProductModelNo, resolveProductName } from '~/utils/tracking/helpers'

/**
 * @description 聚合页活动类型 → 商城 promotionType
 * @type {Record<string, number>}
 */
export const AGGREGATE_ACTIVITY_TYPE_TO_PROMOTION = {
  LIMITED_PRICE: PROMOTION_TYPE.VOLUME_PRICE,
  LIMITED_DISCOUNT: PROMOTION_TYPE.VOLUME_DISCOUNT,
  BUY_GIFT: PROMOTION_TYPE.BUY_GIFT,
  PACKAGE: PROMOTION_TYPE.COMBO_BUNDLE,
  COMBO: PROMOTION_TYPE.COMBO_PROMO,
}

/**
 * @description 聚合页活动区块布局对应的单栏商品展示上限
 * @type {Record<string, number>}
 */
export const AGGREGATE_ACTIVITY_PRODUCT_LIMIT = {
  ONE_PER_ROW: 6,
  TWO_PER_ROW: 3,
}

/** 精品分类栏目普通商品行最多展示商品数：4 个 */
const CATEGORY_SECTION_GRID_PRODUCT_LIMIT = 4

/** 按商品标签配置的聚合栏目，展示逻辑对齐 HotProducts / NewArrivals */
const AGGREGATE_PRODUCT_TAGS = new Set(['HOT', 'NEW', 'SELECTED'])

/** 聚合页活动区移动端单栏最多展示商品数 */
export const AGGREGATE_MOBILE_ACTIVITY_PRODUCT_LIMIT = 3

/**
 * @description 根据聚合页活动布局解析单栏商品展示上限
 * @param {string|null|undefined} layoutType - ONE_PER_ROW | TWO_PER_ROW
 * @returns {number}
 */
export function resolveAggregateActivityProductLimit(layoutType) {
  const normalized = String(layoutType || '')
    .trim()
    .toUpperCase()
  const limit = AGGREGATE_ACTIVITY_PRODUCT_LIMIT[normalized]
  return limit ?? AGGREGATE_ACTIVITY_PRODUCT_LIMIT.TWO_PER_ROW
}

/**
 * @description 解析活动槽位商品展示上限（栏目布局 + 网格是否独占一行）
 * @param {{ layoutType?: string|null }} slot
 * @param {number} gridIndex
 * @param {number} gridTotal
 * @returns {number}
 */
export function resolveActivitySlotProductLimit(slot, gridIndex, gridTotal) {
  const layoutLimit = resolveAggregateActivityProductLimit(slot?.layoutType)
  if (shouldAggregateActivitySpanFullRow(gridIndex, gridTotal)) {
    return AGGREGATE_ACTIVITY_PRODUCT_LIMIT.ONE_PER_ROW
  }
  return layoutLimit
}

/**
 * @description 活动卡片在全局两列网格中是否应独占一行（仅总数为奇数时的最后一项）
 * @param {number} gridIndex
 * @param {number} gridTotal
 * @returns {boolean}
 */
export function shouldAggregateActivitySpanFullRow(gridIndex, gridTotal) {
  const total = Number(gridTotal)
  const index = Number(gridIndex)
  if (!Number.isFinite(total) || !Number.isFinite(index) || total <= 0) return false
  return total % 2 === 1 && index === total - 1
}

/**
 * @description 将聚合页活动区块展平为按展示顺序排列的活动卡片槽位
 * @param {Array<Object>} activityBlocks
 * @returns {Array<{ blockKey: string, activityType: number, title: string, layoutType: string|null, productLimit: number }>}
 */
export function buildAggregateActivitySlots(activityBlocks) {
  const slots = []

  ;(Array.isArray(activityBlocks) ? activityBlocks : []).forEach((block) => {
    const titleMap = block?.activityTitleMap || {}
    const layoutType = block?.layoutType || null
    const productLimit = resolveAggregateActivityProductLimit(layoutType)

    ;(Array.isArray(block?.activityTypes) ? block.activityTypes : []).forEach((activityType) => {
      const type = Number(activityType)
      if (!Number.isFinite(type) || type <= 0) return

      slots.push({
        blockKey: block.key,
        activityType: type,
        title: titleMap[type] || '',
        layoutType,
        productLimit,
      })
    })
  })

  return slots
}

/**
 * @description 按后台 sort 比较聚合页条目（区块 / 商品 / 活动）
 * @param {{ sort?: number, id?: string }} left
 * @param {{ sort?: number, id?: string }} right
 * @returns {number}
 */
export function compareAggregateSort(left, right) {
  const leftSort = Number(left?.sort)
  const rightSort = Number(right?.sort)
  const normalizedLeft = Number.isFinite(leftSort) ? leftSort : 0
  const normalizedRight = Number.isFinite(rightSort) ? rightSort : 0
  if (normalizedLeft !== normalizedRight) {
    return normalizedLeft - normalizedRight
  }
  return String(left?.id || '').localeCompare(String(right?.id || ''))
}

/**
 * @description 获取按 sort 排序后的聚合页区块列表
 * @param {Object|null|undefined} page
 * @returns {Object[]}
 */
export function getSortedAggregateSections(page) {
  const sections = Array.isArray(page?.sections) ? page.sections : []
  return [...sections].sort(compareAggregateSort)
}

/**
 * @description 按 sort 排序区块内商品或活动
 * @param {Object[]} items
 * @returns {Object[]}
 */
export function sortAggregateItems(items) {
  return [...(Array.isArray(items) ? items : [])].sort(compareAggregateSort)
}

/**
 * @description 归一化聚合页接口响应
 * @param {unknown} payload
 * @returns {Object|null}
 */
export function normalizeAggregatePagePayload(payload) {
  if (!payload || typeof payload !== 'object') return null
  if (payload.id || payload.pageKey) return payload
  if (payload.data && typeof payload.data === 'object')
    return normalizeAggregatePagePayload(payload.data)
  return null
}

/**
 * @description 聚合页活动类型映射为 promotionType
 * @param {string} activityType
 * @returns {number|null}
 */
export function mapAggregateActivityTypeToPromotion(activityType) {
  return AGGREGATE_ACTIVITY_TYPE_TO_PROMOTION[String(activityType || '').trim()] ?? null
}

/**
 * @description 从 ACTIVITY 区块构建 promotionType → 后台配置栏目名称映射
 * @param {Object|null|undefined} section
 * @returns {Record<number, string>}
 */
export function buildAggregateActivityTitleMap(section) {
  if (section?.sectionType !== 'ACTIVITY') return {}

  const titleMap = {}

  sortAggregateItems(section?.activities).forEach((activity) => {
    const promotionType = mapAggregateActivityTypeToPromotion(activity?.activityType)
    const title = String(activity?.activityName || activity?.name || '').trim()
    if (!promotionType || !title) return
    titleMap[promotionType] = title
  })

  return titleMap
}

/**
 * @description 从单个 ACTIVITY 区块提取 promotionType（按活动 sort）
 * @param {Object|null|undefined} section
 * @returns {number[]}
 */
export function extractActivityPromotionTypesFromSection(section) {
  if (section?.sectionType !== 'ACTIVITY') return []

  const activities = sortAggregateItems(section?.activities)
  const seen = new Set()
  const types = []

  activities.forEach((activity) => {
    const promotionType = mapAggregateActivityTypeToPromotion(activity?.activityType)
    if (!promotionType || seen.has(promotionType)) return
    seen.add(promotionType)
    types.push(promotionType)
  })

  return types
}

/**
 * @description 从聚合页提取需展示的活动 promotionType（按区块 sort 排序）
 * @param {Object|null|undefined} page
 * @returns {number[]}
 */
export function extractActivityPromotionTypesFromPage(page) {
  const types = []
  const seen = new Set()

  getSortedAggregateSections(page).forEach((section) => {
    extractActivityPromotionTypesFromSection(section).forEach((promotionType) => {
      if (seen.has(promotionType)) return
      seen.add(promotionType)
      types.push(promotionType)
    })
  })

  return types
}

/**
 * @description 聚合页是否配置了可展示的活动模块
 * @param {Object|null|undefined} page
 * @returns {boolean}
 */
export function hasAggregateActivityModule(page) {
  return extractActivityPromotionTypesFromPage(page).length > 0
}

/**
 * @description 判断聚合页商品是否配置了营销图/视频
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export function hasAggregateMarketingMedia(product) {
  return Boolean(String(product?.marketingMedia || product?.mediaUrl || '').trim())
}

/**
 * @description 判断聚合页营销资源是否为视频
 * @param {Object|null|undefined} product
 * @returns {boolean}
 */
export function isAggregateMarketingVideo(product) {
  if (product?.mediaType === 'VIDEO') return true
  const mediaUrl = String(product?.marketingMedia || product?.mediaUrl || '')
  return /\.(mp4|webm|mov|avi)(\?|$)/i.test(mediaUrl)
}

/**
 * @description 拆分栏目商品为第一行 hero 与第二行列表
 * @param {Object[]} products - 已按 sort 排序的原始商品列表
 * @param {Object|null|undefined} section - 聚合页栏目
 * @returns {{ heroProduct: Object|null, gridProducts: Object[] }}
 */
export function splitAggregateSectionProducts(products, section) {
  const sorted = sortAggregateItems(products)
    .map((product) => normalizeAggregateProduct(product))
    .filter(Boolean)

  if (sorted.length === 0) {
    return { heroProduct: null, gridProducts: [] }
  }

  const [first, ...rest] = sorted
  const isCategorySection = section?.sectionType === 'CATEGORY'
  const productTag = String(section?.productTag || '')
    .trim()
    .toUpperCase()
  const isTaggedProductSection = AGGREGATE_PRODUCT_TAGS.has(productTag)
  const heroProduct =
    isCategorySection && !isTaggedProductSection && hasAggregateMarketingMedia(first) ? first : null
  const displayProducts = heroProduct ? rest : sorted
  const gridProducts =
    isCategorySection && !isTaggedProductSection
      ? displayProducts.slice(0, CATEGORY_SECTION_GRID_PRODUCT_LIMIT)
      : displayProducts

  return {
    heroProduct,
    gridProducts,
  }
}

/**
 * @description 构建聚合栏目「查看全部」跳转路由
 * @param {Object|null|undefined} section
 * @returns {{ path: string, query: Record<string, string> }|null}
 */
export function buildAggregateSectionListRoute(section) {
  const categoryId = section?.categoryId
  if (!categoryId) return null
  return {
    path: '/store/products',
    query: { categoryId: String(categoryId) },
  }
}

/**
 * @description 映射单个聚合栏目展示数据
 * @param {Object} section
 * @param {{ pageId?: string, pageKey?: string, pageName?: string, fallbackTitle?: string }} context
 * @returns {Object|null}
 */
export function mapAggregateSectionDisplay(section, context = {}) {
  const { pageId = '', pageKey = '', pageName = '', fallbackTitle = '' } = context
  const { heroProduct, gridProducts } = splitAggregateSectionProducts(section?.products, section)

  if (!heroProduct && gridProducts.length === 0) return null

  return {
    ...section,
    key: `${pageId || pageKey}-${section.id}`,
    pageKey,
    pageName,
    title: section.name || section.categoryName || fallbackTitle || pageName,
    categoryId: section.categoryId || null,
    listRoute: buildAggregateSectionListRoute(section),
    heroProduct,
    gridProducts,
  }
}

/**
 * @description 归一化聚合页商品条目
 * @param {Object|null|undefined} product
 * @returns {Object|null}
 */
export function normalizeAggregateProduct(product) {
  if (!product || typeof product !== 'object') return null

  const prodId = product.prodId || product.id
  if (!prodId) return null

  return {
    ...product,
    id: prodId,
    prodId,
    modelNo: resolveProductModelNo(product) || product.prodCode || '',
    prodName: resolveProductName(product) || product.prodName || product.name || '',
    brief: product.brief || product.description || '',
    pic: product.pic || product.image || '',
    marketingMedia: product.mediaUrl || '',
    mediaType: product.mediaType || '',
    price: product.price,
    oriPrice: product.oriPrice,
  }
}

/**
 * @description Map enabled aggregate-page category sections to the data source used by the category PK page.
 * @reason The PK candidate set is curated in aggregate-page management and must not be replaced by category-wide search results.
 * @param {Object|null|undefined} page - Store aggregate category page.
 * @returns {Array<Object>} Ordered secondary-category sections with normalized configured products.
 */
export function buildAggregateCategoryPkSections(page) {
  const seenCategoryIds = new Set()

  return getSortedAggregateSections(page)
    .map((section) => {
      if (section?.sectionType === 'ACTIVITY') return null

      const categoryId = section?.categoryId
      const categoryName = String(
        section?.categoryName || section?.name || section?.title || ''
      ).trim()
      const products = sortAggregateItems(section?.products)
        .map((product) => normalizeAggregateProduct(product))
        .filter(Boolean)
      const categoryKey = String(categoryId || '')

      if (!categoryKey || !categoryName || products.length === 0) return null
      if (seenCategoryIds.has(categoryKey)) return null
      seenCategoryIds.add(categoryKey)

      return {
        ...section,
        categoryId,
        categoryName,
        prodCount: products.length,
        products,
      }
    })
    .filter(Boolean)
}

/**
 * @description 构建商城聚合页展示时间线（商品区与活动区按 sort 穿插）
 * @param {Object|null|undefined} page
 * @returns {Array<{ type: 'products' | 'activity', key: string, title?: string, layoutType?: string|null, products?: Object[], activityTypes?: number[], activityTitleMap?: Record<number, string>, section: Object, pageKey?: string, pageName?: string }>}
 */
export function buildAggregateDisplayTimeline(page) {
  if (!page) return []

  const pageKey = page.pageKey || ''
  const pageName = page.pageName || ''
  const pageLayoutType = page.layoutType || null

  return getSortedAggregateSections(page)
    .map((section) => {
      const sectionKey = `${page.id || pageKey}-${section.id}`

      if (section?.sectionType === 'ACTIVITY') {
        const activityTypes = extractActivityPromotionTypesFromSection(section)
        if (activityTypes.length === 0) return null
        return {
          type: 'activity',
          key: `activity-${sectionKey}`,
          title: section.name || '',
          layoutType: section.layoutType ?? pageLayoutType ?? null,
          activityTypes,
          activityTitleMap: buildAggregateActivityTitleMap(section),
          section,
        }
      }

      const displaySection = mapAggregateSectionDisplay(section, {
        pageId: page.id,
        pageKey,
        pageName,
      })
      if (!displaySection) return null

      return {
        type: 'products',
        ...displaySection,
        section,
      }
    })
    .filter(Boolean)
}
